// MobileNavDrawer — the <768 hamburger drawer (☰ → drawer).
// Opens from the inline-START edge, anchored under the hamburger icon (flips to
// the right under RTL). No bottom bar, no More-overflow.
//
// Reuses the SlideOver primitive (side="start"; focus trap / Escape / backdrop /
// focus return) — no new primitive. Holds the navigation that collapses on
// phones: the gated domains + the admin sections. The project context (switch)
// is the always-visible left control in the bar, and account lives in the avatar
// menu — neither is duplicated here.
//
// The phone menu leads with the everyday stuff (domains, always expanded);
// admin-on-a-phone is rare, so the whole admin block sits under one collapsible
// "Administration" disclosure — collapsed by default, and opened on first paint
// when you're already on an admin page. Inside it the groups (People / Reference
// data / …) are flat sub-headings; one collapse level is enough on a phone.
//
// The body is a child (`DrawerContent`) so it mounts fresh each time the drawer
// opens (SlideOver unmounts its children when closed) — the Administration
// disclosure's initial open state is then derived per-open with no effect.

import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { icons } from '../ui/icons.js';
import { useCan } from '../../hooks/useCan.js';
import { useEntitlements } from '../../hooks/useEntitlements.js';
import { useMe } from '../../hooks/useMe.js';
import { useSidebarStore } from '../../stores/sidebarStore.js';
import { useToast } from '../ui/Toast.js';
import { domainRoute, isOnDomain, NAV_ITEMS, navItemPath, PLATFORM_HOME, PLATFORM_NAV, platformNavPath, type AreaGroup, type NavItem, visibleAdminSections, visibleNavItems } from '../../config/nav.js';
import { SlideOver } from '../ui/SlideOver.js';
import { NavLink } from '../ui/NavLink.js';
import { PLATFORM_NAME } from '../../branding.js';

function SectionHeading({ children }: { children: string }) {
  return (
    <h2 className="px-3 pt-3 pb-1 text-xs font-semibold uppercase tracking-wide text-[var(--color-text-secondary)]">
      {children}
    </h2>
  );
}

// Platform context — the sysadmin cross-project tabs are the drawer's content.
function PlatformDrawerBody({ close }: { close: () => void }) {
  const { t } = useTranslation('nav');
  return (
    <nav aria-label={t('landmarks.platform')} className="flex flex-col gap-0.5 -mx-1">
      {PLATFORM_NAV.map((item) => {
        const Icon = item.icon;
        return (
          <NavLink key={item.id} to={platformNavPath(item.route)} variant="sidebar" onClick={close}>
            <Icon aria-hidden size={18} className="shrink-0" />
            <span className="truncate">{t(item.labelKey)}</span>
          </NavLink>
        );
      })}
    </nav>
  );
}

// Project context — domains + the collapsible Administration disclosure. Its own
// component so the disclosure hooks (useState / useId) are unconditional (the
// platform vs project split lives in DrawerContent, not behind an early return).
function ProjectDrawerBody({ projectId, close }: { projectId: string | null; close: () => void }) {
  const { t } = useTranslation('nav');
  const location = useLocation();
  const can = useCan(projectId ?? '');
  const entitlements = useEntitlements(projectId ?? '');

  const ctx = { can, entitlements };
  const domains = projectId ? visibleNavItems('primary', ctx) : [];
  const adminSections = projectId ? visibleAdminSections(ctx) : [];
  // A sysadmin's platform door rides the drawer (the gear is desk-only) — the same row the gear's menu holds.
  const isSysadmin = useMe().data?.user.is_sysadmin === true;
  // The AREA groups — a domain whose rail has pages folds them into the drawer
  // as a disclosure (assets · work), from the same registry as the rails.
  const areaItems: Record<AreaGroup, NavItem[]> = {
    assets: projectId ? visibleNavItems('assets', ctx) : [],
    work: projectId ? visibleNavItems('work', ctx) : [],
  };
  const toast = useToast();

  // ONE disclosure open at a time (architect-ruled at T — the drawer is an
  // accordion, the admin treatment generalized): initialized from where you
  // are, and opening either group closes the other.
  const onAdminPage =
    projectId != null &&
    adminSections.some((s) => s.items.some((i) => location.pathname.startsWith(navItemPath(projectId, i.route))));
  // On an area's page — the registry's one predicate (the domain's route or its rail's), the top bar's too.
  const onAreaPage = (area: AreaGroup) => {
    const domain = NAV_ITEMS.find((i) => i.id === area);
    return projectId != null && domain !== undefined && isOnDomain(projectId, domain, location.pathname);
  };
  const [openGroup, setOpenGroup] = useState<AreaGroup | 'admin' | null>(
    onAreaPage('assets') ? 'assets' : onAreaPage('work') ? 'work' : onAdminPage ? 'admin' : null,
  );
  const adminOpen = openGroup === 'admin';
  const adminPanelId = useId();
  const assetsPanelId = useId();
  const workPanelId = useId();
  const areaPanelIds: Record<AreaGroup, string> = { assets: assetsPanelId, work: workPanelId };

  return (
    <div className="flex flex-col gap-0.5 -mx-1">
      {domains.length > 0 && (
        <nav aria-label={t('landmarks.primary')} className="flex flex-col gap-0.5">
          {domains.map((item) => {
            const Icon = item.icon;
            // A domain folds its area rail as a disclosure only when the rail has
            // MORE than one page — a single page IS the domain link; the disclosure
            // appears by itself when a sibling joins (the Work rail: the hub and the Teams).
            const area = (item.id === 'assets' || item.id === 'work') && areaItems[item.id].length > 1 ? (item.id as AreaGroup) : null;
            if (!area) {
              return (
                <NavLink
                  key={item.id}
                  to={navItemPath(projectId!, domainRoute(item, ctx))}
                  end={item.route === ''}
                  variant="sidebar"
                  onClick={close}
                >
                  <Icon aria-hidden size={18} className="shrink-0" />
                  <span className="truncate">{t(item.labelKey)}</span>
                </NavLink>
              );
            }
            // An area domain row is a DISCLOSURE, not a link (T ruling —
            // clicking it navigated to the register while its sub-row was the
            // real page, so parent AND child both wore the active band): the
            // whole row toggles the subtree, the caret rides its end, and only
            // the sub-item carries the active band. On the area's page the
            // parent quietly firms up (text, no band). Its hover is TEXT-ONLY —
            // a fill stayed lit after a tap (the row keeps focus + the pointer;
            // a section toggle is not a destination), the topbar-nav rule.
            const areaOpen = openGroup === area;
            const onThisArea = onAreaPage(area);
            return (
              <div key={item.id}>
                <button
                  type="button"
                  aria-expanded={areaOpen}
                  aria-controls={areaPanelIds[area]}
                  onClick={() => setOpenGroup(areaOpen ? null : area)}
                  className={`flex w-full items-center gap-3 px-3 py-2 min-h-[2.25rem] text-sm text-start transition-colors outline-none rounded-sm focus-ring-inset
                    hover:text-[var(--color-text-primary)]
                    ${onThisArea ? 'font-medium text-[var(--color-text-primary)]' : 'text-[var(--color-text-secondary)]'}`}
                >
                  <Icon aria-hidden size={18} className="shrink-0" />
                  <span className="flex-1 truncate">{t(item.labelKey)}</span>
                  <icons.caretDown
                    aria-hidden
                    size={14}
                    className={`shrink-0 transition-transform ${areaOpen ? 'rotate-180' : ''}`}
                  />
                </button>
                {areaOpen && (
                  <div id={areaPanelIds[area]} role="group" aria-label={t(`${area}Area.title`)} className="flex flex-col gap-0.5">
                    {areaItems[area].map((sub) => {
                      const SubIcon = sub.icon;
                      // ps aligns the sub ICON under the parent LABEL start
                      // (12px pad + 18px glyph + 12px gap) — the tree indent.
                      return sub.soon ? (
                        <button
                          key={sub.id}
                          type="button"
                          onClick={() => toast.info(t('assetsArea.soon'))}
                          className="flex items-center gap-3 ps-[2.625rem] pe-3 py-2 min-h-[2.25rem] text-sm text-start outline-none focus-ring-nav
                            text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-hover)] hover:text-[var(--color-text-primary)]"
                        >
                          <SubIcon aria-hidden size={16} className="shrink-0" />
                          <span className="truncate">{t(sub.labelKey)}</span>
                        </button>
                      ) : (
                        <NavLink
                          key={sub.id}
                          to={navItemPath(projectId!, sub.route)}
                          end
                          variant="sidebar"
                          onClick={close}
                          className="ps-[2.625rem]"
                        >
                          <SubIcon aria-hidden size={16} className="shrink-0" />
                          <span className="truncate">{t(sub.labelKey)}</span>
                        </NavLink>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </nav>
      )}

      {/* Divide "where I go" (domains) from "manage" (admin) — mirrors the
          desktop gear's separateness from the domain nav. */}
      {domains.length > 0 && adminSections.length > 0 && (
        <div role="separator" className="my-1 border-t border-[var(--color-border)]" />
      )}

      {adminSections.length > 0 && (
        <div>
          <button
            type="button"
            aria-expanded={adminOpen}
            aria-controls={adminPanelId}
            onClick={() => setOpenGroup(adminOpen ? null : 'admin')}
            className="w-full flex items-center justify-between gap-2 px-3 pt-3 pb-1 text-xs font-semibold uppercase tracking-wide text-[var(--color-text-primary)] outline-none rounded-sm focus-ring-inset"
          >
            <span>{t('admin.title')}</span>
            <icons.caretDown
              aria-hidden
              size={14}
              className={`shrink-0 transition-transform ${adminOpen ? 'rotate-180' : ''}`}
            />
          </button>
          {adminOpen && (
            <nav id={adminPanelId} aria-label={t('landmarks.admin')} className="flex flex-col gap-0.5">
              {adminSections.map((section) => (
                <div key={section.section} role="group" aria-label={t(section.labelKey)}>
                  <SectionHeading>{t(section.labelKey)}</SectionHeading>
                  {section.items.map((item) => {
                    const Icon = item.icon;
                    return (
                      <NavLink
                        key={item.id}
                        to={item.external ? item.route : navItemPath(projectId!, item.route)}
                        external={item.external}
                        variant="sidebar"
                        onClick={close}
                      >
                        <Icon aria-hidden size={18} className="shrink-0" />
                        <span className="truncate">{t(item.labelKey)}</span>
                      </NavLink>
                    );
                  })}
                </div>
              ))}
            </nav>
          )}
        </div>
      )}

      {isSysadmin && (
        <>
          <div role="separator" className="my-1 border-t border-[var(--color-border)]" />
          <nav aria-label={t('gear.platform')} className="flex flex-col gap-0.5">
            <NavLink to={PLATFORM_HOME} variant="sidebar" onClick={close}>
              <icons.settings aria-hidden size={18} className="shrink-0" />
              <span className="truncate">{t('gear.platform')}</span>
            </NavLink>
          </nav>
        </>
      )}
    </div>
  );
}

// Routes to the right body by context (hooks above the branch stay unconditional).
function DrawerContent({ projectId, close }: { projectId: string | null; close: () => void }) {
  const location = useLocation();
  const { data: me } = useMe();
  if (location.pathname.startsWith('/platform')) {
    // Non-sysadmins can't reach /platform; render nothing rather than project nav.
    return me?.user.is_sysadmin ? (
      <PlatformDrawerBody close={close} />
    ) : (
      <div className="flex flex-col gap-0.5 -mx-1" />
    );
  }
  return <ProjectDrawerBody projectId={projectId} close={close} />;
}

export function MobileNavDrawer({ projectId }: { projectId: string | null }) {
  const { t } = useTranslation('nav');
  const location = useLocation();
  const open = useSidebarStore((s) => s.mobileNavOpen);
  const setOpen = useSidebarStore((s) => s.setMobileNavOpen);
  const close = () => setOpen(false);
  const { data: me } = useMe();

  // The drawer header names the current context — in a project, its name (no
  // demo badge; the demo signal lives in the ProjectBanner); in the platform
  // context, the platform label (the gear's platform door key, so they stay
  // coherent); else the platform brand.
  const isPlatform = location.pathname.startsWith('/platform');
  const current = projectId ? me?.availableProjects.find((p) => p.id === projectId) : undefined;
  const title = isPlatform ? t('gear.platform') : (current?.name ?? PLATFORM_NAME);

  return (
    <SlideOver open={open} onClose={close} title={title} side="start">
      <DrawerContent projectId={projectId} close={close} />
    </SlideOver>
  );
}
